/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

export enum ChatState {
  IDLE,
  GENERATING,
  THINKING,
  EXECUTING,
}

const ICON_BUSY = html`<svg class="rotating" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="currentColor"><path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q17 0 28.5 11.5T520-840q0 17-11.5 28.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-17 11.5-28.5T840-520q17 0 28.5 11.5T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Z"/></svg>`;

@customElement('chat-view')
export class ChatView extends LitElement {
  @property({ type: Array }) messages: HTMLElement[] = [];
  @property({ type: Number }) chatState = ChatState.IDLE;
  @query('#anchor') anchor?: HTMLDivElement;
  @state() private inputMessage = '';

  createRenderRoot() {
    return this;
  }

  updated() {
    this.scrollToBottom();
  }

  private scrollToBottom() {
    if (this.anchor) {
      this.anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  private sendMessage() {
    const msg = this.inputMessage.trim();
    if (!msg || this.chatState !== ChatState.IDLE) return;
    
    this.dispatchEvent(new CustomEvent('send-message', { detail: msg }));
    this.inputMessage = '';
  }

  setInputValue(val: string) {
    this.inputMessage = val;
  }

  render() {
    return html`
      <div id="chat" class="tabcontent showtab">
        <div class="chat-messages">
          ${this.messages}
          <div id="anchor"></div>
        </div>

        <div class="footer">
          <div id="chatStatus" class=${classMap({ hidden: this.chatState === ChatState.IDLE })}>
            ${this.chatState !== ChatState.IDLE ? html`${ICON_BUSY} ${this.getStatusLabel()}` : ''}
          </div>
          <div id="inputArea">
            <input
              type="text"
              id="messageInput"
              .value=${this.inputMessage}
              @input=${(e: any) => this.inputMessage = e.target.value}
              @keydown=${this.handleKeyDown}
              placeholder="Type your message..."
              autocomplete="off"
            />
            <button
              id="sendButton"
              class=${classMap({ disabled: this.chatState !== ChatState.IDLE })}
              @click=${this.sendMessage}
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="30" viewBox="0 -960 960 960" width="30" fill="currentColor">
                <path d="M120-160v-240l320-80-320-80v-240l760 320-760 320Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private getStatusLabel() {
    switch (this.chatState) {
      case ChatState.GENERATING: return 'Generating...';
      case ChatState.THINKING: return 'Thinking...';
      case ChatState.EXECUTING: return 'Executing...';
      default: return '';
    }
  }
}
